/**
 * Creative detail page with video player and analysis results
 */

import { useQueryClient, useMutation } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { useMemo, useRef, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';

import {
  DecisionCard,
  TimelineTable,
  BrandMetricsCard,
  QualityGateCard,
  EditListCard,
  AnalysisLoading,
  DiagnosisCard,
  ExecutiveSummaryCard,
  PerformanceContextCard,
  AnalysisVersionSelector,
  HypothesesCard,
  ImageAnalysisView,
} from '@/components/analysis';
import { PerformanceMetricsPanel } from '@/components/analytics/PerformanceMetricsPanel';
import { PerformanceMetricsForm } from '@/components/creative/PerformanceMetricsForm';
import { VideoPlayer, type VideoPlayerRef } from '@/components/creative/VideoPlayer';
import { TaxonomyValueDisplay } from '@/components/taxonomy';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { ConfirmDeleteModal } from '@/components/ui/ConfirmDeleteModal';
import { SkeletonAnalysis } from '@/components/ui/Skeleton';
import { useToast } from '@/components/ui/Toast';
import {
  analysesKeys,
  useAnalysisList,
  useAnalysis,
  useTriggerAnalysis,
  useAnalysisStatus,
} from '@/hooks/useAnalysis';
import {
  useCreative,
  useUpdateCreativeMetrics,
  usePerformanceContext,
  useDeleteCreative,
} from '@/hooks/useCreatives';
import { useProject } from '@/hooks/useProjects';
import { creativesKeys } from '@/lib/queryClient';
import { timestampToSeconds } from '@/lib/timeUtils';
import { api, updateTaxonomyValues, reExtractTaxonomy } from '@/services/api';
import { formatOptimizationGoal } from '@/services/metricsService';
import type { PerformanceMetrics, PlacementData, Placement, AnalysisResult } from '@/types';
import { isImageAnalysisResult } from '@/types';
import type { ObjectiveBucket } from '@/types/metrics';

/**
 * Display all placements as individual badges
 */
function PlacementBadges({ placements }: { placements: PlacementData[] }) {
  if (!placements || placements.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5">
      {placements.map((p, i) => (
        <span
          key={`${p.placement}-${i}`}
          className="px-2.5 py-1 rounded-md text-xs font-medium bg-purple-50 text-purple-700"
        >
          {p.placement}
        </span>
      ))}
    </div>
  );
}

/**
 * Get all placements from unified field or legacy fields
 */
function getAllPlacements(creative: {
  placements?: PlacementData[];
  metaPlacement?: string;
  metadata?: { placement?: string };
}): PlacementData[] {
  // New unified field takes precedence
  if (creative.placements && creative.placements.length > 0) {
    return creative.placements;
  }
  // Fall back to legacy fields
  if (creative.metaPlacement) {
    return [{ placement: creative.metaPlacement as Placement }];
  }
  if (creative.metadata?.placement) {
    return [{ placement: creative.metadata.placement as Placement }];
  }
  return [];
}

export function CreativeDetail() {
  const { creativeId } = useParams<{ creativeId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const videoPlayerRef = useRef<VideoPlayerRef>(null);
  const [currentVideoTime, setCurrentVideoTime] = useState(0);
  const [activeTab, setActiveTab] = useState<'edits' | 'metrics' | 'insights'>('edits');
  const [showMetricsForm, setShowMetricsForm] = useState(false);
  const [selectedAnalysisId, setSelectedAnalysisId] = useState<string | undefined>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);
  const [metricScope, setMetricScope] = useState<"all" | "bucket" | "goal" | undefined>();
  const [projectScope, setProjectScope] = useState<"project" | "team">("project");
  const [dateRange, setDateRange] = useState<{ startDate?: string; endDate?: string }>({});
  const [peerMediaType, setPeerMediaType] = useState<"video" | "image" | undefined>();

  // Handle aspect ratio detection from video
  const handleAspectRatioDetected = useCallback((landscape: boolean) => {
    setIsLandscape(landscape);
  }, []);

  // Delete creative mutation
  const deleteCreativeMutation = useDeleteCreative(creativeId || '');

  // Fetch creative data
  const { data: creative, isLoading: isLoadingCreative, error: creativeError } = useCreative(
    creativeId || ''
  );

  // Fetch performance context for percentile comparison (scope-aware)
  const { data: performanceContext, isLoading: isLoadingContext } = usePerformanceContext(
    creativeId,
    metricScope,
    projectScope,
    dateRange.startDate && dateRange.endDate ? dateRange.startDate : undefined,
    dateRange.startDate && dateRange.endDate ? dateRange.endDate : undefined,
    peerMediaType,
  );

  // Fetch project to get taxonomy schema
  const { data: project } = useProject(creative?.projectId || '');

  // Update metrics mutation
  const updateMetricsMutation = useUpdateCreativeMetrics(creativeId || '');

  // Fetch all analyses for this creative (for version history)
  const { data: analyses = [], isLoading: isLoadingAnalyses } = useAnalysisList(creativeId);

  // Fetch the selected analysis (or latest if none selected)
  const effectiveAnalysisId = selectedAnalysisId || analyses[0]?.analysisId;
  const { data: selectedAnalysis } = useAnalysis(effectiveAnalysisId);

  // Trigger analysis mutation
  const triggerAnalysisMutation = useTriggerAnalysis();

  // Taxonomy value mutation - update single value
  const updateTaxonomyMutation = useMutation({
    mutationFn: async ({ fieldId, optionId }: { fieldId: string; optionId: string }) => {
      if (!creativeId) throw new Error('No creative ID');
      return updateTaxonomyValues(creativeId, [{ fieldId, optionId }]);
    },
    onSuccess: () => {
      // Invalidate analyses to refresh taxonomy data
      queryClient.invalidateQueries({
        queryKey: analysesKeys.list({ creativeId }),
      });
    },
  });

  // On-demand window reach fetch — calls Meta for the exact date range, caches result
  const windowReachMutation = useMutation({
    mutationFn: async () => {
      if (!creativeId || !creative?.projectId || !dateRange.startDate || !dateRange.endDate) {
        throw new Error('Missing required fields for window reach fetch');
      }
      return api.post(
        `/projects/${creative.projectId}/creatives/${creativeId}/window-reach`,
        { startDate: dateRange.startDate, endDate: dateRange.endDate }
      );
    },
    onSuccess: () => {
      // Invalidate performance context so the cached WINDOWAGGREGATE row is picked up
      queryClient.invalidateQueries({
        queryKey: [...creativesKeys.detail(creativeId || ''), 'performanceContext'],
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to fetch reach data from Meta');
    },
  });

  // Re-extract taxonomy mutation
  const reExtractMutation = useMutation({
    mutationFn: async (preserveUserConfirmed: boolean) => {
      if (!creativeId) throw new Error('No creative ID');
      return reExtractTaxonomy(creativeId, preserveUserConfirmed);
    },
    onSuccess: () => {
      // Clear selected analysis to show the latest one
      setSelectedAnalysisId(undefined);
      // Invalidate analyses to pick up new analysis
      queryClient.invalidateQueries({
        queryKey: analysesKeys.list({ creativeId }),
      });
      queryClient.invalidateQueries({
        queryKey: creativesKeys.detail(creativeId || ''),
      });
    },
  });

  // Poll for analysis status if processing
  const triggeredAnalysis = triggerAnalysisMutation.data;
  const isProcessing = (a?: { status?: string }): boolean =>
    !!a?.status && a.status !== 'complete' && a.status !== 'failed';
  const latestAnalysis = analyses[0];
  const shouldPoll = isProcessing(triggeredAnalysis) || isProcessing(latestAnalysis);
  const analysisIdToPoll = triggeredAnalysis?.analysisId || latestAnalysis?.analysisId;

  const { data: processingAnalysis } = useAnalysisStatus(
    analysisIdToPoll,
    creativeId,
    {
      enabled: shouldPoll && !!analysisIdToPoll,
      onComplete: () => {
        triggerAnalysisMutation.reset();
        // Clear selected analysis to show the latest one
        setSelectedAnalysisId(undefined);
        queryClient.invalidateQueries({
          queryKey: analysesKeys.list({ creativeId }),
        });
        queryClient.invalidateQueries({
          queryKey: creativesKeys.detail(creativeId || ''),
        });
      },
      onError: (analysis) => {
        console.error('Analysis failed:', analysis.errorMessage);
        triggerAnalysisMutation.reset();
        queryClient.invalidateQueries({
          queryKey: analysesKeys.list({ creativeId }),
        });
      },
    }
  );

  // When user explicitly selects a version, honor that selection over processing status.
  // Only show processingAnalysis when no explicit selection has been made.
  const currentAnalysis = selectedAnalysisId
    ? (selectedAnalysis || latestAnalysis)
    : (processingAnalysis || selectedAnalysis || latestAnalysis);

  // Memoize startTime to prevent useEffect restarts in AnalysisLoading
  const analysisStartTime = triggeredAnalysis?.requestedAt || currentAnalysis?.requestedAt;
  const analysisStartDate = useMemo(
    () => (analysisStartTime ? new Date(analysisStartTime) : undefined),
    [analysisStartTime]
  );

  // Handle analyze button click
  const handleAnalyze = async () => {
    if (!creativeId) return;

    try {
      await triggerAnalysisMutation.mutateAsync({ creativeId });
    } catch (error) {
      console.error('Failed to trigger analysis:', error);
    }
  };

  // Handle timeline row click - seek video
  const handleTimelineRowClick = (timestamp: string) => {
    const seconds = timestampToSeconds(timestamp);
    videoPlayerRef.current?.seekTo(seconds);
  };

  // Handle edit timestamp click - seek video
  const handleEditTimestampClick = (timestamp: string) => {
    // Handle range timestamps like "00:05-00:08" by taking the start time
    const parts = timestamp.split('-');
    const startTimestamp = parts[0]?.trim() || timestamp;
    const seconds = timestampToSeconds(startTimestamp);
    videoPlayerRef.current?.seekTo(seconds);
  };

  // Handle video time update
  const handleVideoTimeUpdate = (time: number) => {
    setCurrentVideoTime(time);
  };

  // Handle save performance metrics
  const handleSaveMetrics = async (metrics: PerformanceMetrics) => {
    try {
      await updateMetricsMutation.mutateAsync(metrics);
      setShowMetricsForm(false);
    } catch (error) {
      console.error('Failed to save metrics:', error);
    }
  };

  // Handle delete creative
  const handleDeleteCreative = async () => {
    try {
      await deleteCreativeMutation.mutateAsync();
      setShowDeleteModal(false);
      // Navigate back to project dashboard
      if (creative?.projectId) {
        navigate(`/projects/${creative.projectId}`);
      } else {
        navigate(-1);
      }
    } catch (error) {
      console.error('Failed to delete creative:', error);
    }
  };

  // Loading state
  if (isLoadingCreative) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-blue-600 mb-4" />
          <p className="text-gray-600">Loading creative...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (creativeError || !creative) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Creative not found</h2>
          <p className="text-gray-600 mb-4">
            The creative you&apos;re looking for doesn&apos;t exist or has been deleted.
          </p>
          <Button onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </div>
    );
  }

  const isMetaImported = !!creative?.metaAdId;
  const isAnalyzing = triggerAnalysisMutation.isPending || shouldPoll;
  const isAnalyzed = currentAnalysis?.status === 'complete' && currentAnalysis.result;
  const analysisFailed = currentAnalysis?.status === 'failed';

  return (
    <div className="bg-gray-50 -mx-8 -mt-8 min-h-[calc(100vh-72px)]">
      {/* Header */}
      <div className="bg-surface border-b border-gray-200 sticky -top-8 z-10 pt-8">
        <div className="max-w-7xl mx-auto px-14 py-4 bg-surface">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>

              <div>
                <h1 className="text-2xl font-black text-gray-900">{creative.name}</h1>
                <div className="flex items-center gap-2 mt-1">
                  <Link
                    to={`/projects/${creative.projectId}`}
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    View Project
                  </Link>
                  <span className="text-gray-300">•</span>
                  <span className="text-sm text-gray-500">
                    Uploaded {new Date(creative.createdAt).toLocaleDateString()}
                  </span>
                </div>
                {/* Objective, Optimization Goal, and Placements */}
                {(getAllPlacements(creative).length > 0 || creative.metadata?.objective || creative.metaOptimizationGoal || !creative.metaAdId) && (
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    {creative.metadata?.objective && (
                      <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700">
                        {creative.metadata.objective}
                      </span>
                    )}
                    {creative.metaOptimizationGoal && (
                      <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-teal-50 text-teal-700">
                        {formatOptimizationGoal(creative.metaOptimizationGoal)}
                      </span>
                    )}
                    {!creative.metaOptimizationGoal && !creative.metaAdId && (
                      <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-gray-50 text-gray-500">
                        Manual Upload
                      </span>
                    )}
                    <PlacementBadges placements={getAllPlacements(creative)} />
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Version Selector - show when multiple analyses exist */}
              {analyses.length > 0 && (
                <AnalysisVersionSelector
                  analyses={analyses}
                  currentAnalysisId={currentAnalysis?.analysisId}
                  onSelectAnalysis={setSelectedAnalysisId}
                  isLoading={isLoadingAnalyses}
                />
              )}

              {!isAnalyzed && !isAnalyzing && !isLoadingAnalyses && (
                <Button
                  onClick={handleAnalyze}
                  disabled={triggerAnalysisMutation.isPending}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {triggerAnalysisMutation.isPending ? 'Starting...' : 'Analyze'}
                </Button>
              )}

              {isAnalyzed && (
                <Button
                  onClick={handleAnalyze}
                  disabled={triggerAnalysisMutation.isPending || isAnalyzing}
                  variant="secondary"
                >
                  Re-analyze
                </Button>
              )}

              {/* Delete button */}
              <Button
                variant="ghost"
                onClick={() => setShowDeleteModal(true)}
                className="text-gray-400 hover:text-red-500 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteCreative}
        title="Delete Creative"
        description="This action cannot be undone. The creative and all associated analysis data will be permanently deleted."
        itemName={creative.name}
        confirmText="Delete Creative"
        isLoading={deleteCreativeMutation.isPending}
      />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-14 py-8">
        {/* Shared states: loading, no analysis, analyzing, failed */}
        {isLoadingAnalyses && !currentAnalysis && !isAnalyzing && (
          <SkeletonAnalysis />
        )}

        {!currentAnalysis && !isAnalyzing && !isLoadingAnalyses && (
          <div className="bg-surface rounded-xl border border-gray-200 p-8 text-center max-w-xl mx-auto">
            <svg
              className="w-16 h-16 mx-auto text-gray-300 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <h3 className="text-lg font-bold text-gray-900 mb-2">No analysis yet</h3>
            <p className="text-sm text-gray-600 mb-6">
              Get AI-powered insights on this creative&apos;s effectiveness and recommendations
              for improvement.
            </p>
            <Button onClick={handleAnalyze} disabled={triggerAnalysisMutation.isPending}>
              {triggerAnalysisMutation.isPending ? 'Starting Analysis...' : 'Analyze Creative'}
            </Button>
          </div>
        )}

        {isAnalyzing && (
          <AnalysisLoading
            startTime={analysisStartDate}
          />
        )}

        {analysisFailed && (
          <div className="bg-surface rounded-xl border border-red-200 p-8 text-center max-w-xl mx-auto">
            <svg
              className="w-16 h-16 mx-auto text-red-400 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Analysis Failed</h3>
            <p className="text-sm text-gray-600 mb-6">
              {currentAnalysis?.errorMessage || 'An error occurred during analysis.'}
            </p>
            <Button onClick={handleAnalyze}>Try Again</Button>
          </div>
        )}

        {/* Analysis Complete - Branch by media type */}
        {isAnalyzed && currentAnalysis.result && (() => {
          // Determine if this is an image creative
          const isImage = creative.mediaType === 'image' ||
            ['jpg', 'png', 'webp'].includes(creative.format || '');

          if (isImage && isImageAnalysisResult(currentAnalysis.result)) {
            // Image analysis layout: ImageAnalysisView handles its own grid
            return (
              <ImageAnalysisView
                result={currentAnalysis.result}
                creative={creative}
                analysis={currentAnalysis}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                showMetricsForm={showMetricsForm}
                setShowMetricsForm={setShowMetricsForm}
                metricScope={metricScope}
                projectScope={projectScope}
                setMetricScope={setMetricScope}
                setProjectScope={setProjectScope}
                performanceContext={performanceContext}
                isLoadingContext={isLoadingContext}
                isMetaImported={isMetaImported}
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                onDateRangeChange={setDateRange}
                peerMediaType={peerMediaType}
                onPeerMediaTypeChange={setPeerMediaType}
                handleSaveMetrics={handleSaveMetrics}
                updateMetricsMutation={updateMetricsMutation}
                project={project}
                updateTaxonomyMutation={updateTaxonomyMutation}
                reExtractMutation={reExtractMutation}
              />
            );
          }

          // Video analysis layout: existing two-column with VideoPlayer
          const videoResult = currentAnalysis.result as AnalysisResult;
          return (
            <div className="grid grid-cols-12 gap-6 items-start">
              {/* Left Column - Video Player (sticky for both portrait and landscape) */}
              <div className={`${isLandscape ? 'col-span-5' : 'col-span-4'} sticky top-24 self-start`}>
                <VideoPlayer
                  ref={videoPlayerRef}
                  src={creative.downloadUrl || ''}
                  poster={creative.thumbnailUrl}
                  onTimeUpdate={handleVideoTimeUpdate}
                  onAspectRatioDetected={handleAspectRatioDetected}
                  className={`w-full ${isLandscape ? 'aspect-video max-h-[40vh]' : 'aspect-[9/16] max-h-[70vh]'}`}
                />
              </div>

              {/* Right Column - Video Analysis Results */}
              <div className={isLandscape ? 'col-span-7' : 'col-span-8'}>
                <div className="space-y-6">
                  {/* Decision Card */}
                  <DecisionCard
                    decision={videoResult.decision}
                    executiveSummary={videoResult.executiveSummary}
                  />

                  {/* Tabs - Consolidated: Edits (default), Metrics, Insights */}
                  <div className="bg-surface rounded-xl border border-gray-200 overflow-hidden">
                    <div className="border-b border-gray-200">
                      <div className="flex">
                        <button
                          onClick={() => setActiveTab('edits')}
                          className={`flex-1 px-6 py-3 text-sm font-semibold transition-colors ${
                            activeTab === 'edits'
                              ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                          }`}
                        >
                          Edits
                          {videoResult.edits.length > 0 && (
                            <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                              {videoResult.edits.length}
                            </span>
                          )}
                        </button>
                        <button
                          onClick={() => setActiveTab('metrics')}
                          className={`flex-1 px-6 py-3 text-sm font-semibold transition-colors ${
                            activeTab === 'metrics'
                              ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                          }`}
                        >
                          Metrics
                        </button>
                        <button
                          onClick={() => setActiveTab('insights')}
                          className={`flex-1 px-6 py-3 text-sm font-semibold transition-colors ${
                            activeTab === 'insights'
                              ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                          }`}
                        >
                          Insights
                        </button>
                      </div>
                    </div>

                    {/* Tab Content - No nested scrolling */}
                    <div className="p-6">
                      {activeTab === 'edits' && (
                        <EditListCard
                          edits={videoResult.edits}
                          onTimestampClick={handleEditTimestampClick}
                        />
                      )}

                      {activeTab === 'metrics' && (
                        <div className="space-y-6">
                          {/* Funnel-Aware Performance Metrics Display */}
                          {(creative.performanceMetrics || (isMetaImported && performanceContext?.hasMetrics)) && (
                            <PerformanceMetricsPanel
                              objective={creative.metadata?.objective as ObjectiveBucket | null}
                              performanceMetrics={(creative.performanceMetrics || {}) as Record<string, number | null>}
                              isMetaImported={isMetaImported}
                              performanceContext={isMetaImported ? performanceContext ?? null : null}
                              metricScope={metricScope}
                              projectScope={projectScope}
                              onMetricScopeChange={setMetricScope}
                              onProjectScopeChange={setProjectScope}
                              mediaType={creative.mediaType}
                              startDate={dateRange.startDate}
                              endDate={dateRange.endDate}
                              onDateRangeChange={setDateRange}
                              peerMediaType={peerMediaType}
                              onPeerMediaTypeChange={setPeerMediaType}
                              onFetchWindowReach={() => windowReachMutation.mutate()}
                              isFetchingWindowReach={windowReachMutation.isPending}
                            />
                          )}

                          {/* Performance Metrics Form Section */}
                          {showMetricsForm ? (
                            <Card className="p-6">
                              <h3 className="text-sm font-semibold text-gray-900 mb-4">
                                {creative.performanceMetrics ? 'Edit' : 'Add'} Performance Metrics
                              </h3>
                              <PerformanceMetricsForm
                                initialData={creative.performanceMetrics}
                                onSubmit={handleSaveMetrics}
                                onCancel={() => setShowMetricsForm(false)}
                                isLoading={updateMetricsMutation.isPending}
                              />
                            </Card>
                          ) : (
                            <>
                              {/* Standalone PerformanceContextCard ONLY for manual uploads */}
                              {!isMetaImported && performanceContext && (
                                <PerformanceContextCard
                                  data={performanceContext}
                                  onAddMetrics={() => setShowMetricsForm(true)}
                                  isLoading={isLoadingContext}
                                />
                              )}
                              {creative.performanceMetrics && (
                                <div className="flex justify-end">
                                  <button
                                    onClick={() => setShowMetricsForm(true)}
                                    className="text-sm font-medium text-blue-600 hover:text-blue-700"
                                  >
                                    Edit Metrics
                                  </button>
                                </div>
                              )}
                            </>
                          )}

                          {/* Brand Metrics from Analysis */}
                          <BrandMetricsCard
                            metrics={videoResult.brandMetrics}
                            videoDuration={creative.duration}
                          />
                          <QualityGateCard
                            qualityGate={videoResult.qualityGate}
                            templateSnapshot={currentAnalysis.templateSnapshot}
                          />
                        </div>
                      )}

                      {activeTab === 'insights' && (
                        <div className="space-y-6">
                          {/* Taxonomy Classification - Only show if project has a taxonomy schema */}
                          {project?.taxonomySchema && (
                            <TaxonomyValueDisplay
                              schema={project.taxonomySchema}
                              extraction={currentAnalysis.taxonomyExtraction ?? null}
                              onUpdateValue={async (fieldId, optionId) => {
                                await updateTaxonomyMutation.mutateAsync({ fieldId, optionId });
                              }}
                              onReExtract={async (preserveUserConfirmed) => {
                                await reExtractMutation.mutateAsync(preserveUserConfirmed);
                              }}
                              isUpdating={updateTaxonomyMutation.isPending}
                              isReExtracting={reExtractMutation.isPending}
                            />
                          )}

                          {/* Executive Summary */}
                          <ExecutiveSummaryCard summary={videoResult.executiveSummary} />

                          {/* Diagnosis */}
                          <DiagnosisCard
                            diagnosis={videoResult.diagnosis}
                            templateSnapshot={currentAnalysis.templateSnapshot}
                          />

                          {/* Hypotheses */}
                          <HypothesesCard hypotheses={videoResult.hypotheses} />

                          {/* Timeline - Reference Material */}
                          <div className="pt-4 border-t border-gray-200">
                            <h3 className="text-sm font-semibold text-gray-900 mb-4">Scene-by-Scene Timeline</h3>
                            <TimelineTable
                              timeline={videoResult.timeline}
                              currentTime={currentVideoTime}
                              onRowClick={handleTimelineRowClick}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
